import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faPen,
  faTimes,
  faTrashCan,
} from '@fortawesome/pro-light-svg-icons';
import { Card, ErrorBanner, EmptyState, CharCount, formatDate } from './shared';
import {
  Template,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from './api';
import { MAX_DESCRIPTION, MAX_TEMPLATE_NAME } from './constants';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const Toolbar = styled.div`
  display: flex;
  gap: ${SPACERS.small};
  align-items: center;
  margin: ${SPACERS.small} 0 ${SPACERS.medium};
  flex-wrap: wrap;
  .search {
    flex: 1;
    min-width: 220px;
  }
  .search-input {
    padding: 9px 12px;
    font-size: 14px;
    height: 40px;
    box-sizing: border-box;
  }
  .toolbar-btn {
    height: 40px;
  }
`;

const FieldMeta = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 4px 0 ${SPACERS.small};
`;

type EditorState =
  | { mode: 'create' }
  | { mode: 'edit'; template: Template }
  | null;

const TemplatesTab: React.FC = () => {
  const [rows, setRows] = useState<Template[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editor, setEditor] = useState<EditorState>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<Template | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTemplates({
        limit: perPage,
        offset: page * perPage,
        q: appliedSearch.trim() || undefined,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(
        (err instanceof Error && err.message) || 'Failed to load templates.',
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, appliedSearch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setName('');
    setDescription('');
    setEditorError(null);
    setEditor({ mode: 'create' });
  };

  const openEdit = (template: Template) => {
    setName(template.name);
    setDescription(template.description);
    setEditorError(null);
    setEditor({ mode: 'edit', template });
  };

  const save = async () => {
    if (!editor) return;
    setSaving(true);
    setEditorError(null);
    try {
      if (editor.mode === 'create') {
        await createTemplate(name.trim(), description.trim());
      } else {
        await updateTemplate(editor.template.id, {
          name: name.trim(),
          description: description.trim(),
        });
      }
      setEditor(null);
      refresh();
    } catch (err) {
      setEditorError(
        (err instanceof Error && err.message) || 'Failed to save template.',
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await deleteTemplate(deleting.id);
      setDeleting(null);
      refresh();
    } catch (err) {
      setError(
        (err instanceof Error && err.message) || 'Failed to delete template.',
      );
      setDeleting(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const canSave = name.trim().length > 0 && description.trim().length > 0;

  const openBulk = () => {
    setBulkText('');
    setBulkError(null);
    setBulkProgress(null);
    setBulkOpen(true);
  };

  const parseBulk = (text: string): { name: string; description: string }[] => {
    const trimmed = text.trim();
    if (!trimmed) throw new Error('Input is empty.');
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      throw new Error('Invalid JSON.');
    }
    if (!Array.isArray(parsed)) {
      throw new Error('Input must be a JSON array.');
    }
    return parsed.map((item, idx) => {
      if (!item || typeof item !== 'object') {
        throw new Error(`Item ${idx + 1} is not an object.`);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const name = (item as any).name;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const description = (item as any).description;
      if (typeof name !== 'string' || !name.trim()) {
        throw new Error(`Item ${idx + 1} is missing "name".`);
      }
      if (typeof description !== 'string' || !description.trim()) {
        throw new Error(`Item ${idx + 1} is missing "description".`);
      }
      if (name.length > MAX_TEMPLATE_NAME) {
        throw new Error(
          `Item ${idx + 1} name exceeds ${MAX_TEMPLATE_NAME} chars.`,
        );
      }
      if (description.length > MAX_DESCRIPTION) {
        throw new Error(
          `Item ${idx + 1} description exceeds ${MAX_DESCRIPTION} chars.`,
        );
      }
      return { name: name.trim(), description: description.trim() };
    });
  };

  const validation = useMemo((): {
    status: 'empty' | 'valid' | 'invalid';
    message: string;
    count?: number;
  } => {
    const trimmed = bulkText.trim();
    if (!trimmed) return { status: 'empty', message: '' };
    try {
      parseBulk(trimmed);
      const count = (JSON.parse(trimmed) as unknown[]).length;
      return {
        status: 'valid',
        message: `Valid JSON — ${count} template${
          count === 1 ? '' : 's'
        } ready.`,
        count,
      };
    } catch (err) {
      return {
        status: 'invalid',
        message: (err instanceof Error && err.message) || 'Invalid input.',
      };
    }
  }, [bulkText]);

  const beautifyBulk = () => {
    const trimmed = bulkText.trim();
    if (!trimmed) return;
    try {
      const parsed = JSON.parse(trimmed);
      setBulkText(JSON.stringify(parsed, null, 2));
      setBulkError(null);
    } catch {
      setBulkError('Cannot beautify: invalid JSON.');
    }
  };

  const submitBulk = async () => {
    setBulkError(null);
    let items: { name: string; description: string }[];
    try {
      items = parseBulk(bulkText);
    } catch (err) {
      setBulkError(
        (err instanceof Error && err.message) || 'Failed to parse input.',
      );
      return;
    }
    setBulkSaving(true);
    setBulkProgress({ done: 0, total: items.length });
    const failures: string[] = [];
    for (let i = 0; i < items.length; i += 1) {
      try {
        await createTemplate(items[i].name, items[i].description);
      } catch (err) {
        failures.push(
          `"${items[i].name}": ${(err instanceof Error && err.message) ||
            'failed to create'}`,
        );
      }
      setBulkProgress({ done: i + 1, total: items.length });
    }
    setBulkSaving(false);
    if (failures.length) {
      setBulkError(
        `Created ${items.length - failures.length} of ${
          items.length
        }. Errors:\n${failures.join('\n')}`,
      );
      refresh();
      return;
    }
    setBulkOpen(false);
    refresh();
  };

  return (
    <Card>
      <TrueImpactText variant="heading-subheading">Templates</TrueImpactText>
      <Toolbar>
        <div className="search">
          <TrueImpactTextfield
            className="search-input"
            placeholder="Search name or description…"
            value={search}
            onChange={v => setSearch(v)}
            onEnterPress={() => {
              setPage(0);
              setAppliedSearch(search);
            }}
          />
        </div>
        <TrueImpactButton
          className="toolbar-btn"
          variant="secondary-small"
          onClick={() => {
            setPage(0);
            setAppliedSearch(search);
          }}
        >
          Search
        </TrueImpactButton>
        <TrueImpactButton
          className="toolbar-btn"
          variant="secondary-small"
          onClick={openBulk}
        >
          Bulk Add
        </TrueImpactButton>
        <TrueImpactButton
          className="toolbar-btn"
          variant="primary-small"
          onClick={openCreate}
        >
          + New Template
        </TrueImpactButton>
      </Toolbar>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {loading && rows.length === 0 ? (
        <TrueImpactLoading />
      ) : rows.length === 0 ? (
        <EmptyState>
          <div className="icon">
            <FontAwesomeIcon icon={faFileLines} />
          </div>
          <TrueImpactText>
            {appliedSearch
              ? 'No templates match your search.'
              : 'No templates yet. Create one to get started.'}
          </TrueImpactText>
        </EmptyState>
      ) : (
        <>
          <TableContainer
            style={{ opacity: loading ? 0.6 : 1, transition: '0.2s' }}
          >
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell
                      style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      {t.name}
                    </TableCell>
                    <TableCell>{t.description}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(t.updated_at)}
                    </TableCell>
                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        title="Edit"
                        onClick={() => openEdit(t)}
                      >
                        <FontAwesomeIcon
                          icon={faPen}
                          style={{ fontSize: 14 }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Delete"
                        onClick={() => setDeleting(t)}
                      >
                        <FontAwesomeIcon
                          icon={faTrashCan}
                          style={{ fontSize: 14 }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={perPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRowsPerPageChange={e => {
              setPage(0);
              setPerPage(parseInt(e.target.value, 10));
            }}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                marginBottom: SPACERS.extraTiny,
              },
            }}
          />
        </>
      )}

      <Dialog
        open={!!editor}
        onClose={() => !saving && setEditor(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: 'inherit',
            fontSize: 18,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {editor?.mode === 'edit' ? 'Edit Template' : 'New Template'}
          <IconButton
            size="small"
            onClick={() => !saving && setEditor(null)}
            disabled={saving}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: 14 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TrueImpactText
            variant="body-small-bold"
            style={{ display: 'block', marginBottom: 4 }}
          >
            Name
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="Template name"
            value={name}
            maxLength={MAX_TEMPLATE_NAME}
            onChange={v => setName(v)}
          />
          <FieldMeta>
            <CharCount over={name.length >= MAX_TEMPLATE_NAME}>
              {name.length} / {MAX_TEMPLATE_NAME}
            </CharCount>
          </FieldMeta>
          <TrueImpactText
            variant="body-small-bold"
            style={{ display: 'block', marginBottom: 4 }}
          >
            Description
          </TrueImpactText>
          <TrueImpactTextfield
            isTextarea
            height={120}
            placeholder="What is this template for?"
            value={description}
            maxLength={MAX_DESCRIPTION}
            onChange={v => setDescription(v)}
          />
          <FieldMeta>
            <CharCount over={description.length >= MAX_DESCRIPTION}>
              {description.length} / {MAX_DESCRIPTION}
            </CharCount>
          </FieldMeta>
          {editorError && <ErrorBanner>{editorError}</ErrorBanner>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <TrueImpactButton
            variant="secondary-large"
            onClick={() => setEditor(null)}
            disabled={saving}
            style={{ flex: 1, padding: '10px 16px', fontSize: 14 }}
          >
            Cancel
          </TrueImpactButton>
          <TrueImpactButton
            variant="primary-large"
            onClick={save}
            disabled={!canSave || saving}
            style={{ flex: 1, padding: '10px 16px', fontSize: 14 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </TrueImpactButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleting}
        onClose={() => !deleteLoading && setDeleting(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ fontFamily: 'inherit', fontSize: 18, fontWeight: 600 }}
        >
          Delete Template
        </DialogTitle>
        <DialogContent>
          <TrueImpactText variant="body-small">
            Are you sure you want to delete “{deleting?.name}”? This cannot be
            undone.
          </TrueImpactText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <TrueImpactButton
            variant="secondary-large"
            onClick={() => setDeleting(null)}
            disabled={deleteLoading}
            style={{ flex: 1, padding: '10px 16px', fontSize: 14 }}
          >
            Cancel
          </TrueImpactButton>
          <TrueImpactButton
            variant="primary-large"
            backgroundColor="critical"
            onClick={confirmDelete}
            disabled={deleteLoading}
            style={{ flex: 1, padding: '10px 16px', fontSize: 14 }}
          >
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </TrueImpactButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkOpen}
        onClose={() => !bulkSaving && setBulkOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: 'inherit',
            fontSize: 18,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Bulk Add Templates
          <IconButton
            size="small"
            onClick={() => !bulkSaving && setBulkOpen(false)}
            disabled={bulkSaving}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: 14 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TrueImpactText
            variant="body-small"
            color="grayDark"
            style={{ display: 'block', marginBottom: SPACERS.small }}
          >
            Paste a JSON array of templates, each with <code>name</code> and{' '}
            <code>description</code>. Example:{' '}
            <code>
              [{'{'}"name": "...", "description": "..."{'}'}]
            </code>
          </TrueImpactText>
          <TrueImpactTextfield
            isTextarea
            height={240}
            placeholder={
              '[\n  { "name": "Landing page", "description": "A marketing landing page" },\n  { "name": "Blog post", "description": ' +
                '"A long-form blog article" }\n]'
            }
            value={bulkText}
            onChange={v => setBulkText(v)}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: SPACERS.small,
              marginTop: 4,
            }}
          >
            <TrueImpactText
              variant="body-small"
              color={
                validation.status === 'valid'
                  ? 'success'
                  : validation.status === 'invalid'
                  ? 'critical'
                  : 'grayDark'
              }
            >
              {validation.status === 'empty'
                ? 'Paste JSON above to validate.'
                : validation.status === 'valid'
                ? `✓ ${validation.message}`
                : `✗ ${validation.message}`}
            </TrueImpactText>
            <TrueImpactButton
              variant="secondary-small"
              onClick={beautifyBulk}
              disabled={bulkSaving || !bulkText.trim()}
              style={{ height: 32 }}
            >
              Beautify JSON
            </TrueImpactButton>
          </div>
          {bulkProgress && (
            <TrueImpactText
              variant="body-small"
              color="grayDark"
              style={{ display: 'block', marginTop: SPACERS.small }}
            >
              Progress: {bulkProgress.done} / {bulkProgress.total}
            </TrueImpactText>
          )}
          {bulkError && (
            <ErrorBanner style={{ marginTop: SPACERS.small }}>
              {bulkError}
            </ErrorBanner>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <TrueImpactButton
            variant="secondary-large"
            onClick={() => setBulkOpen(false)}
            disabled={bulkSaving}
            style={{ flex: 1, padding: '10px 16px', fontSize: 14 }}
          >
            Cancel
          </TrueImpactButton>
          <TrueImpactButton
            variant="primary-large"
            onClick={submitBulk}
            disabled={bulkSaving || validation.status !== 'valid'}
            style={{ flex: 1, padding: '10px 16px', fontSize: 14 }}
          >
            {bulkSaving ? 'Adding…' : 'Add All'}
          </TrueImpactButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TemplatesTab;
